import { api, HttpError, launchHref } from "../api";
import { el } from "../lib/dom";
import { accessBadge, errorBox, heroBand } from "../lib/ui";
import { navigate } from "../router";
import type { AppDetail } from "../types";

function requestAccessForm(app: AppDetail, onDone: () => void): HTMLElement {
  const message = el("textarea", {
    class: "control-input",
    rows: "3",
    placeholder: "Why do you need access? (required)",
  }) as HTMLTextAreaElement;
  const submit = el("button", { class: "btn btn-primary", type: "button", disabled: "true" }, [
    "Request access",
  ]) as HTMLButtonElement;
  const hint = el("p", { class: "muted-sm" }, ["A reason is required (at least 10 characters)."]);
  const feedback = el("p", { class: "muted" });

  const validate = () => {
    if (message.value.trim().length >= 10) submit.removeAttribute("disabled");
    else submit.setAttribute("disabled", "true");
  };
  message.addEventListener("input", validate);

  submit.addEventListener("click", async () => {
    const reason = message.value.trim();
    if (reason.length < 10) return validate();
    submit.setAttribute("disabled", "true");
    try {
      await api.requestAccess(app.key, reason);
      feedback.textContent = "Request submitted — an admin will review it.";
      onDone();
    } catch (e) {
      feedback.textContent =
        e instanceof HttpError && e.status === 409
          ? "You already have a request or access for this app."
          : e instanceof HttpError && e.status === 400
            ? "A reason (at least 10 characters) is required."
            : "Couldn't submit the request. Try again.";
      submit.removeAttribute("disabled");
    }
  });

  const kids: (HTMLElement | string)[] = [el("h3", {}, ["Request access"])];
  if (app.last_request_status === "denied" && app.last_request_note) {
    kids.push(
      el("div", { class: "denied-note" }, [
        el("strong", {}, ["Previously declined: "]),
        app.last_request_note,
        el("p", { class: "muted-sm" }, ["You can request again — add more detail below."]),
      ]),
    );
  }
  kids.push(message, hint, submit, feedback);
  return el("div", { class: "detail-action" }, kids);
}

function launchAction(app: AppDetail): HTMLElement {
  return el("div", { class: "detail-action" }, [
    el(
      "a",
      {
        class: "btn btn-primary btn-lg",
        href: launchHref(app.key),
        target: "_blank",
        rel: "noopener noreferrer",
      },
      [`Launch ${app.name} ↗`],
    ),
  ]);
}

function credentialsAction(app: AppDetail): HTMLElement {
  const creds = app.credentials!;
  const feedback = el("span", { class: "muted-sm" });

  function copyButton(label: string, value: string): HTMLElement {
    const b = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, [`Copy ${label}`]);
    b.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(value);
        feedback.textContent = `${label} copied`;
      } catch {
        feedback.textContent = `${label}: ${value}`;
      }
    });
    return b;
  }

  function credField(label: string, value: string): HTMLElement {
    return el("div", { class: "cred-field" }, [
      el("span", { class: "cred-label" }, [label]),
      el("code", { class: "cred-value" }, [value]),
      copyButton(label.toLowerCase(), value),
    ]);
  }

  return el("div", { class: "detail-action cred-card" }, [
    el("h3", {}, ["Sign-in details"]),
    el("p", { class: "muted-sm" }, [
      `Copy the username and password below, then Open ${app.name} and paste them on its login.`,
    ]),
    el("div", { class: "cred-fields" }, [
      credField("Username", creds.username),
      credField("Password", creds.password),
    ]),
    el("div", { class: "cred-row" }, [
      el(
        "a",
        {
          class: "btn btn-primary btn-lg",
          href: launchHref(app.key),
          target: "_blank",
          rel: "noopener noreferrer",
        },
        [`Open ${app.name} ↗`],
      ),
      feedback,
    ]),
  ]);
}

function actionFor(app: AppDetail, reload: () => void): HTMLElement {
  if (app.access_status === "granted") {
    if (app.launch_strategy === "credentials" && app.credentials) return credentialsAction(app);
    return launchAction(app);
  }
  if (app.access_status === "pending") {
    return el("div", { class: "detail-action" }, [
      el("p", { class: "pending-note" }, ["Your access request is pending admin approval."]),
    ]);
  }
  // Governed app: no self-serve request — explain how to get access instead.
  if (app.self_request === false) {
    return el("div", { class: "detail-action governed-note" }, [
      el("h3", {}, ["Access by approval"]),
      el("p", {}, [
        app.access_note ||
          `Access to ${app.name} is granted by an administrator. Please request it through your manager for approval.`,
      ]),
    ]);
  }
  return requestAccessForm(app, reload);
}

function statsSection(stats: AppDetail["stats"]): HTMLElement {
  return el("section", { class: "detail-section" }, [
    el("h3", {}, ["By the numbers"]),
    el(
      "div",
      { class: "stats-grid" },
      stats.map((s) =>
        el("div", { class: "stat" }, [
          el("div", { class: "stat-value" }, [s.value]),
          el("div", { class: "stat-label" }, [s.label]),
        ]),
      ),
    ),
  ]);
}

function listSection(title: string, items: string[]): HTMLElement {
  return el("section", { class: "detail-section" }, [
    el("h3", {}, [title]),
    el(
      "ul",
      { class: "cap-list" },
      items.map((i) => el("li", {}, [i])),
    ),
  ]);
}

function chipsSection(title: string, items: string[]): HTMLElement {
  return el("section", { class: "detail-section" }, [
    el("h3", {}, [title]),
    el(
      "div",
      { class: "chips" },
      items.map((i) => el("span", { class: "chip" }, [i])),
    ),
  ]);
}

function stub(title: string, note: string): HTMLElement {
  return el("section", { class: "detail-section detail-stub" }, [
    el("h3", {}, [title]),
    el("p", { class: "muted-sm" }, [note]),
  ]);
}

function fmtDuration(s: number): string {
  if (!isFinite(s) || s <= 0) return "";
  const m = Math.floor(s / 60);
  return `${m}:${Math.round(s % 60).toString().padStart(2, "0")}`;
}

/** Demo video with Short/Full tabs (switch either way; each tab shows its duration).
 * Source is Hub's login-gated signed-redirect endpoint (never a public URL). */
function demoSection(app: AppDetail): HTMLElement {
  const video = el("video", {
    class: "demo-video",
    controls: "",
    preload: "metadata",
    playsinline: "",
  }) as HTMLVideoElement;

  const variants = [{ id: "short", label: "Short clip" }];
  if (app.has_demo_video_full) variants.push({ id: "full", label: "Full walkthrough" });

  const tabEls: { id: string; el: HTMLButtonElement }[] = [];
  function select(id: string, autoplay: boolean): void {
    video.src = `/api/apps/${app.key}/video?variant=${id}`;
    if (autoplay) void video.play();
    for (const t of tabEls) t.el.classList.toggle("is-active", t.id === id);
  }

  const tabs = variants.map((v) => {
    const dur = el("span", { class: "video-tab-dur" });
    const btn = el("button", { class: "video-tab", type: "button" }, [
      el("span", {}, [v.label]),
      dur,
    ]) as HTMLButtonElement;
    btn.addEventListener("click", () => select(v.id, true));
    tabEls.push({ id: v.id, el: btn });
    // metadata-only probe (through the gated endpoint) to label the tab with its length
    const probe = document.createElement("video");
    probe.preload = "metadata";
    probe.src = `/api/apps/${app.key}/video?variant=${v.id}`;
    probe.addEventListener("loadedmetadata", () => {
      const d = fmtDuration(probe.duration);
      if (d) dur.textContent = d;
    });
    return btn;
  });

  const children: HTMLElement[] = [el("h3", {}, ["Demo"])];
  if (variants.length > 1) children.push(el("div", { class: "video-tabs" }, tabs));
  children.push(video);
  select("short", false);
  return el("section", { class: "detail-section" }, children);
}

function overviewSection(text: string): HTMLElement {
  const paras = text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  return el("section", { class: "detail-section" }, [
    el("h3", {}, ["Overview"]),
    ...paras.map((p) => el("p", { class: "overview-p" }, [p])),
  ]);
}

function faqSection(faqs: { q: string; a: string }[]): HTMLElement {
  return el("section", { class: "detail-section" }, [
    el("h3", {}, ["FAQ"]),
    ...faqs.map((f) =>
      el("details", { class: "faq-item" }, [el("summary", {}, [f.q]), el("p", { class: "faq-a" }, [f.a])]),
    ),
  ]);
}

/** Architecture diagram. Mermaid is lazy-loaded (own chunk) and rendered with
 * securityLevel:"strict" so app-supplied diagram source can't inject HTML/JS. */
function architectureSection(source: string): HTMLElement {
  const diagram = el("div", { class: "arch-diagram" }, ["Rendering diagram…"]);
  void (async () => {
    try {
      const mermaid = (await import("mermaid")).default;
      mermaid.initialize({ startOnLoad: false, theme: "neutral", securityLevel: "strict" });
      const { svg } = await mermaid.render("arch-" + Math.random().toString(36).slice(2), source);
      diagram.innerHTML = svg;
    } catch {
      diagram.textContent = "";
      diagram.append(el("pre", { class: "arch-src" }, [source]));
    }
  })();
  return el("section", { class: "detail-section" }, [el("h3", {}, ["Architecture"]), diagram]);
}

/** "Ask about this app" — a chat grounded on the app's profile (Claude, via /api/apps/{key}/ask). */
function askSection(app: AppDetail): HTMLElement {
  const logEl = el("div", { class: "ask-log" });
  const input = el("input", {
    class: "ask-input",
    type: "text",
    placeholder: `Ask anything about ${app.name}…`,
  }) as HTMLInputElement;
  const send = el("button", { class: "ask-send", type: "button" }, ["Ask"]) as HTMLButtonElement;
  const history: { role: string; content: string }[] = [];
  let busy = false;

  function bubble(role: string, text: string): HTMLElement {
    const b = el("div", { class: `ask-msg ask-${role}` }, [text]);
    logEl.append(b);
    logEl.scrollTop = logEl.scrollHeight;
    return b;
  }

  async function ask(): Promise<void> {
    const q = input.value.trim();
    if (!q || busy) return;
    busy = true;
    input.value = "";
    input.disabled = send.disabled = true;
    bubble("user", q);
    const thinking = bubble("assistant", "…");
    thinking.classList.add("is-thinking");
    try {
      const { answer } = await api.askApp(app.key, q, history);
      thinking.textContent = answer;
      history.push({ role: "user", content: q }, { role: "assistant", content: answer });
    } catch {
      thinking.textContent = "Sorry — the assistant is unavailable right now.";
    } finally {
      thinking.classList.remove("is-thinking");
      busy = false;
      input.disabled = send.disabled = false;
      input.focus();
    }
  }

  send.addEventListener("click", () => void ask());
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") void ask();
  });

  return el("section", { class: "detail-section" }, [
    el("h3", {}, ["Ask about this app"]),
    el("p", { class: "muted-sm" }, [
      `Grounded on ${app.name}'s profile — ask what it does, how it works, or how to use it.`,
    ]),
    logEl,
    el("div", { class: "ask-row" }, [input, send]),
  ]);
}

/** Super-admin control: publish a Preview app to everyone, or stage a published
 * one back into Preview. (API enforces require_admin; this just hides the UI.) */
function visibilityBar(app: AppDetail, reload: () => void): HTMLElement {
  const preview = app.is_visible === false;
  const btn = el(
    "button",
    { class: preview ? "btn btn-primary" : "btn btn-ghost btn-sm", type: "button" },
    [preview ? "Publish to everyone" : "Move to Preview"],
  ) as HTMLButtonElement;
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    try {
      await api.adminSetVisibility(app.key, preview); // preview → publish(true); published → hide(false)
      reload();
    } catch {
      btn.disabled = false;
    }
  });
  return el("div", { class: `preview-admin-bar${preview ? "" : " is-published"}` }, [
    el("span", { class: "preview-badge" }, [preview ? "Preview" : "Published"]),
    el("span", { class: "muted-sm" }, [
      preview
        ? "Only super-admins can see this app. Publishing makes it available to everyone per its access settings."
        : "Visible to everyone.",
    ]),
    btn,
  ]);
}

export async function renderAppDetail(key: string): Promise<HTMLElement> {
  const root = el("main", { class: "page detail" });

  let app: AppDetail;
  try {
    app = await api.getApp(key);
  } catch (e) {
    if (e instanceof HttpError && e.status === 404) {
      root.append(heroBand("App", "not found", "That app isn't in the catalogue."));
      root.append(el("a", { class: "btn btn-ghost", href: "/", "data-link": "" }, ["Back to apps"]));
    } else {
      root.append(errorBox("Couldn't load this app."));
    }
    return root;
  }

  const me = await api.whoami().catch(() => null);

  root.append(el("a", { class: "back-link", href: "/", "data-link": "" }, ["← All apps"]));
  root.append(heroBand(app.name, "", app.tagline || app.description));
  root.append(
    el("div", { class: "detail-meta" }, [
      el("span", { class: "tile-type" }, [app.app_type]),
      accessBadge(app.access_status),
    ]),
  );
  if (me?.is_admin) {
    root.append(visibilityBar(app, () => navigate(`/apps/${app.key}`)));
  }
  if (app.maintenance) {
    root.append(
      el("div", { class: "maintenance-banner detail-maint" }, [
        `🔧 Under maintenance — ${app.maintenance_message || "this app is temporarily unavailable. Launch and access requests are paused."}`,
      ]),
    );
  }
  root.append(actionFor(app, () => navigate(`/apps/${app.key}`)));

  const sections = el("div", { class: "detail-sections" });
  if (app.tagline) sections.append(listSection("About", [app.description]));
  if (app.overview) sections.append(overviewSection(app.overview));
  if (app.has_demo_video) sections.append(demoSection(app));
  if (app.stats.length) sections.append(statsSection(app.stats));
  if (app.highlights.length) sections.append(listSection("Key capabilities", app.highlights));
  if (app.architecture) sections.append(architectureSection(app.architecture));
  if (app.tech_stack.length) sections.append(chipsSection("Tech stack", app.tech_stack));
  if (app.faqs?.length) sections.append(faqSection(app.faqs));
  if (!app.has_demo_video)
    sections.append(stub("Screenshots", "Screens and walkthroughs — coming soon."));
  sections.append(askSection(app));
  root.append(sections);
  return root;
}
